import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import ar from './locales/ar.json';
import zh from './locales/zh.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import vi from './locales/vi.json';
import th from './locales/th.json';

const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'ar', 'zh', 'ja', 'ko', 'vi', 'th'];

// Get saved language or default to 'en'
const getSavedLanguage = () => {
  // Check if we're in browser environment
  if (typeof window !== 'undefined' && window.localStorage) {
    const saved = localStorage.getItem('passportTalk_primaryLanguage');
    if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
      return saved;
    }
  }
  return 'en';
};

// Update document direction for RTL languages
export const updateDocumentDirection = (lang) => {
  if (typeof document === 'undefined') return;
  
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = lang;
  
  // Add/remove RTL class for styling
  if (lang === 'ar') {
    document.documentElement.classList.add('rtl');
  } else {
    document.documentElement.classList.remove('rtl');
  }
};

// Initialize language from localStorage on app startup
export const initializeLanguage = () => {
  const savedLang = getSavedLanguage();
  if (savedLang && savedLang !== i18n.language) {
    i18n.changeLanguage(savedLang);
  }
  updateDocumentDirection(savedLang);
  return savedLang;
};

// Language to source_lang mapping for translations
export const languageToSourceLang = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  ar: 'ar',
  zh: 'zh',
  ja: 'ja',
  ko: 'ko',
  vi: 'vi',
  th: 'th'
};

// Get default translation direction based on user's language
export const getDefaultTranslationDirection = (userLang) => {
  if (userLang === 'en') {
    return { source: 'en', target: 'es' }; // English users translate TO Spanish
  }
  // Non-English users translate FROM their language TO English
  return { source: userLang, target: 'en' };
};

const initialLanguage = getSavedLanguage();

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      ar: { translation: ar },
      zh: { translation: zh },
      ja: { translation: ja },
      ko: { translation: ko },
      vi: { translation: vi },
      th: { translation: th }
    },
    lng: initialLanguage,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false // React already handles XSS
    },
    react: {
      useSuspense: false
    }
  });

// Set initial direction immediately
updateDocumentDirection(initialLanguage);

// Listen for language changes
i18n.on('languageChanged', (lng) => {
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.setItem('passportTalk_primaryLanguage', lng);
  }
  updateDocumentDirection(lng);
});

export default i18n;
